import { useCallback } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export type CaseParty = {
  id: string;
  case_id: string;
  legal_entity_id: string;

  side: string;
  role: string;
  is_primary: number;

  notes: string | null;

  client_id: string | null;
  entity_type: string;

  name: string;
  document: string | null;
  email: string | null;
  phone: string | null;
};

export type LegalEvent = {
  id: string;

  event_kind: string;
  event_type: string;

  client_id: string | null;
  case_id: string | null;

  entity_type: string | null;
  entity_id: string | null;

  title: string | null;
  description: string | null;

  actor_email: string | null;
  actor_name: string | null;

  occurred_at: string;
  created_at: string;

  before?: unknown;
  after?: unknown;

  metadata?: Record<string, unknown>;
};

export type ConflictMatch = {
  entity_id: string;
  client_id: string | null;
  entity_type: string;

  name: string;
  document: string | null;

  party_id: string | null;
  case_id: string | null;

  side: string | null;
  role: string | null;

  case_title: string | null;
  case_number: string | null;
  case_status: string | null;
};

export type ConflictResult = {
  success: boolean;

  status:
    | 'clear'
    | 'review';

  matchCount: number;

  matches:
    ConflictMatch[];

  checkId: string;
};

async function readJsonResponse(
  response: Response
) {
  const data =
    await response
      .json()
      .catch(() => ({}));

  if (
    !response.ok ||
    data?.success !== true
  ) {
    throw new Error(
      data?.error ||
        `A requisição falhou com status ${response.status}.`
    );
  }

  return data;
}

export function useLegalCore() {
  const loadCaseParties =
    useCallback(
      async (
        caseId: string
      ) => {
        const response =
          await fetchWithRetry(
            `/api/case-parties?caseId=${encodeURIComponent(
              caseId
            )}`
          );

        const data =
          await readJsonResponse(
            response
          );

        return Array.isArray(
          data.parties
        )
          ? (data.parties as CaseParty[])
          : [];
      },
      []
    );

  const loadCaseEvents =
    useCallback(
      async (
        caseId: string,
        eventKind?:
          | 'timeline'
          | 'audit'
      ) => {
        const params =
          new URLSearchParams({
            caseId,
            limit: '200',
          });

        if (eventKind) {
          params.set(
            'eventKind',
            eventKind
          );
        }

        const response =
          await fetchWithRetry(
            `/api/legal-events?${params.toString()}`
          );

        const data =
          await readJsonResponse(
            response
          );

        return Array.isArray(
          data.events
        )
          ? (data.events as LegalEvent[])
          : [];
      },
      []
    );

  const checkConflict =
    useCallback(
      async (
        name?: string | null,
        document?: string | null,
        excludeCaseId?: string | null
      ) => {
        const response =
          await fetch(
            '/api/conflicts/check',
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify({
                  name:
                    name?.trim() ||
                    null,

                  document:
                    document?.trim() ||
                    null,

                  excludeCaseId:
                    excludeCaseId?.trim() ||
                    null,
                }),
            }
          );

        return (
          await readJsonResponse(
            response
          )
        ) as ConflictResult;
      },
      []
    );

  return {
    loadCaseParties,
    loadCaseEvents,
    checkConflict,
  };
}
